/**
 * AI Coach Helper Functions
 * Pure functions for business logic related to AI Coach sessions
 */

import {
  AI_COACH_INTRO_PART,
  AICoachTriggerErrorCode,
  type AICoachPart,
  type AICoachSessionData
} from '../../../../../pure-js/types/AICoach'
import { normalizeAICoachCompletedParts } from '../../../../../pure-js/libs/AICoachStateHelper'

/**
 * Normalize completed parts for old sessions that existed before the intro part.
 */
export const getNormalizedCompletedParts = (session: AICoachSessionData): AICoachPart[] => {
  return normalizeAICoachCompletedParts(session)
}

/** Key of the cached TTS audio URL for a part (`introUrl` or `segmentNUrl`). */
export const getAudioUrlKeyForPart = (part: AICoachPart): keyof NonNullable<AICoachSessionData['ttsAudio']> =>
  part === AI_COACH_INTRO_PART ? 'introUrl' : (`segment${part}Url` as const)

export const hasAudioForPart = (session: AICoachSessionData | undefined, part: AICoachPart): boolean =>
  !!session?.ttsAudio?.[getAudioUrlKeyForPart(part)]

/**
 * Replay re-speaks the part the team is currently on for everyone. It's only
 * possible when the assistant is idle between steps (or after the final step)
 * and cached audio exists, so it never advances the flow or regenerates audio.
 */
export const canReplayAICoachSession = (session: AICoachSessionData | undefined): boolean => {
  if (!session) return false
  const part = session.currentPart
  if (part === null || part === undefined) return false
  if (session.status !== 'paused' && session.status !== 'completed') return false
  return hasAudioForPart(session, part)
}

/** Aria was already speaking: someone else in the call pressed first. */
export const isAlreadySpeakingRefusal = (error: Error): boolean =>
  error.message === AICoachTriggerErrorCode.AI_ALREADY_SPEAKING
